<template>
  <div>
    <b-row>
      <b-col>
        <b-card class="header-marcar_consulta bg-gradient-primary">
          <b-row align-v="center">
            <b-col lg="4">
              <h1>
                <feather-icon icon="BookOpenIcon" size="30" class="mr-50" />
                Alunos
              </h1>
            </b-col>
            <b-col lg="8">
              <b-form @submit.prevent="onSubmitSearch">
                <b-input-group class="input-group-merge">
                  <b-input-group-prepend is-text>
                    <feather-icon icon="SearchIcon" />
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="filtro.name"
                    placeholder="Buscar Aluno"
                    @input="onSearchInput"
                  />
                </b-input-group>
              </b-form>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <!-- Filtros -->
    <b-card class="mb-2">
      <b-row align-v="center">
        <b-col md="4" class="mb-1 mb-md-0">
          <label class="font-small-3 font-weight-bold">Escola</label>
          <v-select
            v-model="filtro.schoolId"
            :options="escolaOptions"
            :reduce="option => option.value"
            label="text"
            placeholder="Todas as escolas"
            @input="onEscolaChange"
          />
        </b-col>
        <b-col md="4" class="mb-1 mb-md-0">
          <label class="font-small-3 font-weight-bold">Status</label>
          <v-select
            v-model="filtro.approved"
            :options="statusOptions"
            :reduce="option => option.value"
            label="text"
            placeholder="Todos os status"
            @input="onStatusChange"
          />
        </b-col>
      </b-row>
    </b-card>

    <div class="d-flex mb-1" v-if="fetchedStudents.totalItems">
      <div class="align-self-center text-muted">
        Total: <strong>{{ fetchedStudents.totalItems }}</strong> aluno(s)
      </div>
      <b-pagination
        @input="onChangePage"
        class="ml-auto mb-0"
        v-model="currentPage"
        :total-rows="fetchedStudents.totalItems"
        :per-page="perPage"
      ></b-pagination>
    </div>
    <b-card no-body class="mb-0">
      <b-table
        ref="refUserListTable"
        class="position-relative"
        :items="fetchedStudents.content"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        :sort-desc.sync="isSortDirDesc"
        no-local-sorting
        @sort-changed="onSortChanged"
        show-empty
        empty-text="Nenhum aluno encontrado"
      >
        <!-- Column: Escola -->
        <template #cell(school)="data">
          <span>{{ data.item.school?.name || '-' }}</span>
        </template>

        <!-- Column: Respondeu Quiz -->
        <template #cell(respondeuQuiz)="data">
          <b-badge
            pill
            :variant="`light-${resolveQuizStatusVariant(data.item.respondeuQuiz)}`"
            class="text-capitalize"
          >
            {{ resolveQuizStatusText(data.item.respondeuQuiz) }}
          </b-badge>
        </template>

        <!-- Column: Status -->
        <template #cell(approved)="data">
          <b-badge
            pill
            :variant="`light-${resolveUserStatusVariant(data.item.approved)}`"
            class="text-capitalize"
          >
            {{ resolveUserStatusText(data.item.approved) }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              @click="approveStudent(data.item.id, !data.item.approved)"
            >
              <feather-icon :icon="!data.item.approved ? 'CheckIcon' : 'XCircleIcon'" />
              <span class="align-middle ml-50"> {{!data.item.approved ? 'Aprovar' : 'Desaprovar'}} Aluno</span>
            </b-dropdown-item>
            <b-dropdown-item
              @click="abrirModalAlterarTurma(data.item)"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> Alterar Turma</span>
            </b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-item
              variant="danger"
              @click="confirmDeleteStudent(data.item)"
            >
              <feather-icon icon="Trash2Icon" class="text-danger" />
              <span class="align-middle ml-50 text-danger"> Excluir Estudante</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
    </b-card>
    <div class="d-flex mt-1" v-if="fetchedStudents.totalItems">
      <b-pagination
        @input="onChangePage"
        class="ml-auto"
        v-model="currentPage"
        :total-rows="fetchedStudents.totalItems"
        :per-page="perPage"
      ></b-pagination>
    </div>

    <!-- Modal Alterar Turma -->
    <b-modal
      id="modal-alterar-turma"
      title="Alterar Turma do Aluno"
      ok-title="Salvar"
      cancel-title="Cancelar"
      ok-variant="primary"
      cancel-variant="outline-secondary"
      :ok-disabled="!modalTurma.classroomId || salvandoTurma"
      @ok="salvarAlteracaoTurma"
    >
      <div v-if="alunoSelecionado">
        <b-row class="mb-1">
          <b-col cols="12">
            <p class="mb-50">
              <strong>Aluno:</strong> {{ alunoSelecionado.fullName }}
            </p>
            <p class="mb-50">
              <strong>Escola atual:</strong> {{ alunoSelecionado.school?.name || '-' }}
            </p>
            <p class="text-muted small mb-0">
              <strong>Turma atual:</strong> {{ alunoSelecionado.turmaFormatada || alunoSelecionado.classroom || '-' }}
            </p>
          </b-col>
        </b-row>

        <b-form-group label="Nova Escola" label-for="modal-escola">
          <v-select
            id="modal-escola"
            v-model="modalTurma.schoolId"
            :options="todasEscolasOptions"
            :reduce="option => option.value"
            label="text"
            placeholder="Selecione a Escola"
            @input="onModalEscolaChange"
          />
        </b-form-group>

        <b-form-group label="Nova Turma" label-for="modal-turma" class="mb-0">
          <v-select
            id="modal-turma"
            v-model="modalTurma.classroomId"
            :options="turmasOptions"
            :reduce="option => option.value"
            label="text"
            :disabled="!modalTurma.schoolId || carregandoTurmas"
            :placeholder="carregandoTurmas ? 'Carregando turmas...' : (!modalTurma.schoolId ? 'Selecione uma escola primeiro' : 'Selecione a Turma')"
          />
        </b-form-group>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { hideSpinner, showSpinner } from '@/services/spinService'
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownItem,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BModal,
  BPagination,
  BRow,
  BTable,
} from 'bootstrap-vue'
import { Portuguese } from 'flatpickr/dist/l10n/pt'
import usersService from '@/services/usersService'
import schoolService from '@/services/schoolService'
import classroomService from '@/services/classroomService'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { RoleEnum } from '@/utils/constants'
import vSelect from 'vue-select'

export default {
  components: {
    BCard,
    BTable,
    BPagination,
    BDropdown,
    BDropdownItem,
    BBadge,
    BInputGroup,
    BFormInput,
    BInputGroupPrepend,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    BModal,
    vSelect,
  },
  data: () => ({
    configFlatPickr: { dateFormat: 'd/m/Y', locale: Portuguese },
    filtro: {
      name: '',
      schoolId: null,
      approved: null,
    },
    statusOptions: [
      { value: null, text: 'Todos os status' },
      { value: true, text: 'Aprovados' },
      { value: false, text: 'Não Aprovados' },
    ],
    escolaOptions: [],
    perPage: 10,
    totalUsers: 0,
    currentPage: 1,
    perPageOptions: [10, 25, 50, 100],
    searchQuery: '',
    searchDebounce: null,
    sortBy: 'fullName',
    isSortDirDesc: false,
    roleFilter: null,
    planFilter: null,
    statusFilter: null,
    fetchedStudents: { content: [], totalItems: 0 },
    tableColumns: [
      { key: 'fullName', label: 'Nome', sortable: true },
      { key: 'school', label: 'Escola', sortable: true },
      { key: 'email', label: 'Email', sortable: true },
      { key: 'classroom', label: 'Turma', sortable: true },
      { key: 'turno', label: 'Turno', sortable: true },
      { key: 'respondeuQuiz', label: 'Respondeu Quiz', sortable: false, tdClass: 'text-center', thClass: 'text-center' },
      { key: 'approved', label: 'Status', sortable: true },
      { key: 'actions', label: 'Ações', sortable: false },
    ],
    alunoSelecionado: null,
    modalTurma: {
      schoolId: null,
      classroomId: null,
    },
    todasEscolasOptions: [],
    turmasOptions: [],
    carregandoTurmas: false,
    salvandoTurma: false,
  }),
  mounted() {
    this.carregarEscolas()
    this.carregarTodasEscolas()
    this.fetchStudents()
  },
  methods: {
    async carregarEscolas() {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}')
      const isProfessor = userData?.role === 'Professor' || userData?.role === 3

      if (isProfessor && userData?.id) {
        try {
          const profData = await usersService.getProfessorTurmas(userData.id)
          const turmas = profData?.turmas || []
          const escolasMap = new Map()

          turmas.forEach(t => {
            const id = t.escolaId || t.schoolId
            const name = t.escola || t.schoolName
            if (id && name && !escolasMap.has(id)) {
              escolasMap.set(id, { value: id, text: name })
            }
          })

          if (escolasMap.size === 0 && userData.schoolId) {
            escolasMap.set(userData.schoolId, {
              value: userData.schoolId,
              text: userData.school?.name || 'Minha Escola',
            })
          }

          this.escolaOptions = Array.from(escolasMap.values())
        } catch (error) {
          console.error('Erro ao carregar escolas do professor:', error)
          this.escolaOptions = []
        }
      } else {
        try {
          const res = await schoolService.getAll({ page: 1, itemsPerPage: 1000 })
          const list = res.content || res || []
          this.escolaOptions = list.map(e => ({ value: e.id, text: e.name }))
        } catch (error) {
          console.error('Erro ao carregar escolas:', error)
          this.escolaOptions = []
        }
      }
    },
    async carregarTodasEscolas() {
      try {
        const res = await schoolService.getAll({ page: 1, itemsPerPage: 1000 })
        const list = res.content || res || []
        this.todasEscolasOptions = list.map(e => ({ value: e.id, text: e.name }))
      } catch (error) {
        console.error('Erro ao carregar lista de todas as escolas:', error)
      }
    },
    onEscolaChange() {
      this.currentPage = 1
      this.fetchStudents()
    },
    onStatusChange() {
      this.currentPage = 1
      this.fetchStudents()
    },
    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.isSortDirDesc = ctx.sortDesc
      this.currentPage = 1
      this.fetchStudents()
    },
    onSearchInput() {
      clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.currentPage = 1
        this.fetchStudents()
      }, 400)
    },
    onSubmitSearch() {
      clearTimeout(this.searchDebounce)
      this.currentPage = 1
      this.fetchStudents()
    },
    onChangePage() {
      this.fetchStudents()
    },
    prepareRequest(obj) {
      return {
        ...obj,
        role: RoleEnum.Student,
        page: this.currentPage,
        itemsPerPage: this.perPage,
        orderBy: this.sortBy ? `${this.sortBy} ${this.isSortDirDesc ? 'desc' : 'asc'}` : undefined,
        schoolId: this.filtro.schoolId || undefined,
        approved: this.filtro.approved !== null && this.filtro.approved !== undefined ? this.filtro.approved : undefined,
      }
    },
    async fetchStudents() {
      showSpinner()
      try {
        this.fetchedStudents = await usersService.getAllAlunos(
          this.prepareRequest(this.filtro)
        )
      } catch (error) {
        console.error('Erro ao carregar alunos:', error)
      } finally {
        hideSpinner()
      }
    },
    resolveUserStatusText: status => (status ? 'Aprovado' : 'Não Aprovado'),

    resolveUserStatusVariant: status => (status ? 'success' : 'danger'),

    resolveQuizStatusText: respondeuQuiz => (respondeuQuiz ? 'Sim' : 'Não'),

    resolveQuizStatusVariant: respondeuQuiz => (respondeuQuiz ? 'success' : 'warning'),

    async approveStudent(id, approved) {
      showSpinner()
      await usersService.approveStudent(id, approved).then(() => {
        this.fetchStudents()
      })
      hideSpinner()
    },

    async abrirModalAlterarTurma(aluno) {
      this.alunoSelecionado = aluno
      const schoolId = aluno.schoolId || aluno.school?.id || null
      const classroomId = aluno.classroomId || null

      this.modalTurma = {
        schoolId,
        classroomId,
      }
      this.turmasOptions = []

      if (!this.todasEscolasOptions.length) {
        await this.carregarTodasEscolas()
      }

      if (schoolId) {
        await this.carregarTurmasModal(schoolId)
        this.modalTurma.classroomId = classroomId
      }

      this.$bvModal.show('modal-alterar-turma')
    },

    async onModalEscolaChange(schoolId) {
      this.modalTurma.classroomId = null
      this.turmasOptions = []
      if (schoolId) {
        await this.carregarTurmasModal(schoolId)
      }
    },

    async carregarTurmasModal(schoolId) {
      this.carregandoTurmas = true
      try {
        const res = await classroomService.getAll({ schoolId, itemsPerPage: 1000 })
        const turmas = res.content || res || []
        this.turmasOptions = turmas.map(t => ({
          value: t.id,
          text: `${t.ano || ''} - ${t.turma || ''} (${t.turno || ''})`.trim(),
        }))
      } catch (error) {
        console.error('Erro ao carregar turmas:', error)
        this.turmasOptions = []
      } finally {
        this.carregandoTurmas = false
      }
    },

    async salvarAlteracaoTurma(bvModalEvt) {
      if (!this.modalTurma.classroomId || !this.alunoSelecionado?.id) {
        if (bvModalEvt) bvModalEvt.preventDefault()
        return
      }

      this.salvandoTurma = true
      showSpinner()
      try {
        await usersService.changeClassroom(this.alunoSelecionado.id, this.modalTurma.classroomId)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Turma alterada com sucesso!',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.$bvModal.hide('modal-alterar-turma')
        await this.fetchStudents()
      } catch (error) {
        console.error('Erro ao alterar turma:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao alterar turma',
            text: error?.response?.data?.message || 'Ocorreu um erro ao tentar alterar a turma.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        if (bvModalEvt) bvModalEvt.preventDefault()
      } finally {
        this.salvandoTurma = false
        hideSpinner()
      }
    },

    confirmDeleteStudent(item) {
      const alunoNome = item.fullName || item.name || 'este aluno'
      this.$bvModal
        .msgBoxConfirm(
          `Tem certeza que deseja excluir o estudante "${alunoNome}"? ATENÇÃO: Todas as respostas de desafios, questionários, arquivos anexos e pontuações ligados a este estudante serão permanentemente excluídos!`,
          {
            title: 'Confirmar Exclusão de Estudante',
            size: 'md',
            okVariant: 'danger',
            okTitle: 'Sim, excluir definitivamente',
            cancelTitle: 'Cancelar',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          },
        )
        .then(value => {
          if (value) {
            this.deleteStudent(item.id)
          }
        })
    },

    async deleteStudent(id) {
      showSpinner()
      try {
        await usersService.delete(id)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Estudante excluído com sucesso!',
            text: 'Todos os registros associados foram removidos.',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        await this.fetchStudents()
      } catch (error) {
        console.error('Erro ao excluir estudante:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao excluir estudante',
            text: error?.response?.data?.message || 'Ocorreu um erro ao tentar excluir o estudante.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        hideSpinner()
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
